import { apiCall, API_BASE_URL } from '../../../utils/apiClient';
import type { GeneratedTestCaseField } from '../types';
import type {
  PendingTestCaseInstancesResponse,
  TestCaseInstance,
  TestCaseInstanceSource,
  TestCaseInstanceStatus,
} from './types';

interface TestCaseInstanceFieldDto {
  key?: string;
  label?: string;
  value?: unknown;
}

export interface TestCaseInstanceDto {
  instanceId?: string;
  instance_id?: string;
  testCaseThreadId?: string;
  test_case_thread_id?: string;
  threadId?: string;
  thread_id?: string;
  testCaseId?: string;
  test_case_id?: string;
  versionNumber?: number;
  version_number?: number;
  source?: string;
  status?: string;
  createdAt?: string;
  created_at?: string;
  feedbackInstruction?: string;
  feedback_instruction?: string;
  fields?: TestCaseInstanceFieldDto[];
  generatedTestCase?: string;
  generated_test_case?: string;
  text?: string;
  approvedAt?: string;
  approved_at?: string;
}

interface PendingTestCaseInstancesDto {
  current?: TestCaseInstanceDto;
  pending?: TestCaseInstanceDto | TestCaseInstanceDto[];
}

function normalizeSource(source?: string): TestCaseInstanceSource {
  const normalized = (source ?? '')
    .replace('TEST_CASE_INSTANCE_SOURCE_', '')
    .toUpperCase();

  switch (normalized) {
    case 'USER':
    case 'AGENT':
    case 'AI':
      return normalized;

    default:
      throw new Error(
        `Unsupported test case instance source: ${source ?? 'empty'}`,
      );
  }
}

function normalizeStatus(status?: string): TestCaseInstanceStatus {
  const normalized = (status ?? '')
    .replace('TEST_CASE_INSTANCE_STATUS_', '')
    .toUpperCase();

  switch (normalized) {
    case 'CURRENT':
    case 'PENDING_REVIEW':
    case 'SUPERSEDED':
    case 'REJECTED':
    case 'DISCARDED':
    case 'APPROVED':
      return normalized;

    default:
      return 'UNKNOWN';
  }
}

function normalizeFieldValue(value: unknown): string | string[] {
  if (Array.isArray(value)) {
    return value.map(item =>
      typeof item === 'string' ? item : JSON.stringify(item),
    );
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value === undefined || value === null) {
    return '';
  }

  return JSON.stringify(value);
}

function toDisplayLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, character => character.toUpperCase());
}

function normalizeFieldKey(key: string): string {
  return key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

function isStepsKey(key: string): boolean {
  const normalized = normalizeFieldKey(key);
  return normalized === 'steps' || normalized === 'teststeps';
}

function isExpectedResultsKey(key: string): boolean {
  const normalized = normalizeFieldKey(key);
  return normalized === 'expectedresult' || normalized === 'expectedresults';
}

function isStatusKey(key: string): boolean {
  const normalized = normalizeFieldKey(key);
  return normalized === 'status' || normalized === 'testcasestatus';
}

function normalizeItems(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map(item =>
        typeof item === 'string' ? item.trim() : JSON.stringify(item),
      )
      .filter(Boolean);
  }

  if (typeof value !== 'string' || !value.trim()) {
    return [];
  }

  return [value.trim()];
}

function parseGeneratedTestCaseFields(
  generatedTestCase?: string,
): GeneratedTestCaseField[] {
  if (!generatedTestCase) {
    return [];
  }

  try {
    const parsed = JSON.parse(generatedTestCase) as
      | Array<Record<string, unknown>>
      | {
          test_cases?: Array<Record<string, unknown>>;
        }
      | Record<string, unknown>;

    let testCase: Record<string, unknown> | undefined;

    if (Array.isArray(parsed)) {
      testCase = parsed[0];
    } else if (
      Array.isArray(
        (
          parsed as {
            test_cases?: Array<Record<string, unknown>>;
          }
        ).test_cases,
      )
    ) {
      testCase = (
        parsed as {
          test_cases: Array<Record<string, unknown>>;
        }
      ).test_cases[0];
    } else {
      testCase = parsed;
    }

    if (!testCase || Object.keys(testCase).length === 0) {
      return [];
    }

    const fields: GeneratedTestCaseField[] = [];
    const stepsEntry = Object.entries(testCase).find(([key]) =>
      isStepsKey(key),
    );
    const expectedResultsEntry = Object.entries(testCase).find(([key]) =>
      isExpectedResultsKey(key),
    );

    const pairedFields: GeneratedTestCaseField[] = [
      {
        key: 'steps',
        label: stepsEntry ? toDisplayLabel(stepsEntry[0]) : 'Test Steps',
        value: normalizeItems(stepsEntry?.[1]),
      },
      {
        key: 'expectedResults',
        label: expectedResultsEntry
          ? toDisplayLabel(expectedResultsEntry[0])
          : 'Expected Results',
        value: normalizeItems(expectedResultsEntry?.[1]),
      },
    ];

    let pairedFieldsAdded = false;

    Object.entries(testCase).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }

      if (isStatusKey(key)) {
        return;
      }

      if (isStepsKey(key)) {
        if (!pairedFieldsAdded) {
          fields.push(...pairedFields);
          pairedFieldsAdded = true;
        }

        return;
      }

      if (isExpectedResultsKey(key)) {
        return;
      }

      fields.push({
        key,
        label: toDisplayLabel(key),
        value: normalizeFieldValue(value),
      });
    });

    if (!pairedFieldsAdded && (stepsEntry || expectedResultsEntry)) {
      fields.push(...pairedFields);
    }

    return fields;
  } catch {
    return [
      {
        key: 'generatedTestCase',
        label: 'Generated Test Case',
        value: generatedTestCase,
      },
    ];
  }
}

export function mapTestCaseInstanceFields(
  dto: TestCaseInstanceDto,
): GeneratedTestCaseField[] {
  if (dto.fields?.length) {
    return dto.fields.map((field, index) => {
      const key = field.key ?? `field_${index}`;

      return {
        key,
        label: field.label ?? toDisplayLabel(key),
        value: normalizeFieldValue(field.value),
      };
    });
  }

  const generatedTestCase =
    dto.generatedTestCase ?? dto.generated_test_case ?? dto.text;

  return parseGeneratedTestCaseFields(generatedTestCase);
}

function mapInstance(dto: TestCaseInstanceDto): TestCaseInstance {
  const testCaseThreadId =
    dto.testCaseThreadId ??
    dto.test_case_thread_id ??
    dto.threadId ??
    dto.thread_id ??
    '';

  return {
    instanceId: dto.instanceId ?? dto.instance_id ?? '',
    testCaseThreadId,
    testCaseId: dto.testCaseId ?? dto.test_case_id ?? '',
    versionNumber: dto.versionNumber ?? dto.version_number ?? 0,
    source: normalizeSource(dto.source),
    status: normalizeStatus(dto.status),
    createdAt: dto.createdAt ?? dto.created_at ?? '',
    feedbackInstruction: dto.feedbackInstruction ?? dto.feedback_instruction,
    fields: mapTestCaseInstanceFields(dto),
  };
}

function sortInstancesNewestFirst(
  instances: TestCaseInstance[],
): TestCaseInstance[] {
  return [...instances].sort((left, right) => {
    if (right.versionNumber !== left.versionNumber) {
      return right.versionNumber - left.versionNumber;
    }

    const rightDate = Date.parse(right.createdAt);
    const leftDate = Date.parse(left.createdAt);

    return (
      (Number.isNaN(rightDate) ? 0 : rightDate) -
      (Number.isNaN(leftDate) ? 0 : leftDate)
    );
  });
}

async function throwForFailedResponse(
  response: Response,
  action: string,
): Promise<void> {
  if (response.ok) {
    return;
  }

  const errorText = await response.text();
  throw new Error(`${action} failed: ${response.status} ${errorText}`);
}

export async function getPendingTestCaseInstances(
  threadId: string,
): Promise<PendingTestCaseInstancesResponse> {
  const response = await apiCall(
    `${API_BASE_URL}/test-cases/${encodeURIComponent(threadId)}/pending`,
    { method: 'GET' },
  );

  await throwForFailedResponse(response, 'Pending instance load');

  const data = (await response.json()) as PendingTestCaseInstancesDto;
  let pendingDtos: TestCaseInstanceDto[] = [];

  if (Array.isArray(data.pending)) {
    pendingDtos = data.pending;
  } else if (data.pending) {
    pendingDtos = [data.pending];
  }

  const current = data.current ? mapInstance(data.current) : null;

  const pending = sortInstancesNewestFirst(
    pendingDtos
      .map(mapInstance)
      .filter(instance => instance.status === 'PENDING_REVIEW'),
  );

  return {
    current,
    pending,
  };
}

export async function acceptPendingInstance(
  threadId: string,
  instanceId: string,
): Promise<void> {
  const response = await apiCall(
    `${API_BASE_URL}/test-cases/${encodeURIComponent(
      threadId,
    )}/instances/${encodeURIComponent(instanceId)}/accept`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    },
  );

  await throwForFailedResponse(response, 'Accept pending instance');
}

export async function rejectPendingInstance(
  threadId: string,
  instanceId: string,
): Promise<void> {
  const response = await apiCall(
    `${API_BASE_URL}/test-cases/${encodeURIComponent(
      threadId,
    )}/instances/${encodeURIComponent(instanceId)}/reject`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    },
  );

  await throwForFailedResponse(response, 'Reject pending instance');
}

export async function discardPendingInstance(
  threadId: string,
  instanceId: string,
): Promise<void> {
  const response = await apiCall(
    `${API_BASE_URL}/test-cases/${encodeURIComponent(
      threadId,
    )}/instances/${encodeURIComponent(instanceId)}/discard`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    },
  );

  await throwForFailedResponse(response, 'Regenerate pending instance');
}
